/**
 * Configurazione centralizzata degli ambienti
 */

// Funzione per determinare l'ambiente corrente
export function getEnvironment() {
  // In Svelte, NODE_ENV potrebbe non essere disponibile nel browser
  // Usa PUBLIC_NODE_ENV o rileva dall'URL
  const nodeEnv = import.meta.env.NODE_ENV || import.meta.env.PUBLIC_NODE_ENV;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Rilevamento automatico basato su hostname (priorità massima)
  if (hostname.includes('stage') || hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  } else if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'development';
  } else if (hostname.includes('auting.it')) {
    return 'production';
  }

  // Fallback su NODE_ENV
  return nodeEnv || 'development';
}

// Configurazioni per ambiente
const environments = {
  development: {
    name: 'Development',
    growthbook: {
      apiHost: import.meta.env.PUBLIC_GROWTHBOOK_API_HOST || 'https://backend.auting.it:2453',
      clientKey: import.meta.env.PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-0butk45SAQBdrHWw',
      enableDevMode: false,
      timeout: 2000,
      streaming: true
    },
    api: {
      baseUrl: import.meta.env.PUBLIC_API_BASE_URL || 'https://api.auting.it/v3.2',
      oldBaseUrl: import.meta.env.PUBLIC_API_BASE_URL_V1 || 'https://api.auting.it/qpi',
    },
    assets: {
      baseUrl: import.meta.env.PUBLIC_ASSETS_BASE_URL || 'https://assets.auting.it'
    },
    site: {
      url: import.meta.env.PUBLIC_SITE_URL || 'http://localhost:5173'
    },
    features: {
      enableLogging: true,
      enableAnalytics: false,
      enableHotjar: false
    }
  },
  staging: {
    name: 'Staging',
    growthbook: {
      apiHost: import.meta.env.PUBLIC_GROWTHBOOK_API_HOST || 'https://backend.auting.it:2453',
      clientKey: import.meta.env.PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-MArt7yCvKgJtHqdj',
      enableDevMode: true,
      timeout: 3000,
      streaming: true
    },
    api: {
      baseUrl: import.meta.env.PUBLIC_API_BASE_URL || 'https://api.stage.auting.it/v3.2',
      oldBaseUrl: import.meta.env.PUBLIC_API_BASE_URL_V1 || 'https://api.stage.auting.it/v1',
    },
    assets: {
      baseUrl: import.meta.env.PUBLIC_ASSETS_BASE_URL || 'https://assets.stage.auting.it'
    },
    site: {
      url: import.meta.env.PUBLIC_SITE_URL || 'https://stage.auting.it'
    },
    features: {
      enableLogging: true,
      enableAnalytics: true,
      enableHotjar: false
    }
  },
  production: {
    name: 'Production',
    growthbook: {
      apiHost: import.meta.env.PUBLIC_GROWTHBOOK_API_HOST || 'https://backend.auting.it:2453',
      clientKey: import.meta.env.PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-0butk45SAQBdrHWw',
      enableDevMode: false,
      timeout: 2000,
      streaming: true
    },
    api: {
      baseUrl: import.meta.env.PUBLIC_API_BASE_URL || 'https://api.auting.it/v3.2',
      oldBaseUrl: import.meta.env.PUBLIC_API_BASE_URL_V1 || 'https://api.auting.it/qpi',
    },
    assets: {
      baseUrl: import.meta.env.PUBLIC_ASSETS_BASE_URL || 'https://assets.auting.it'
    },
    site: {
      url: import.meta.env.PUBLIC_SITE_URL || 'https://auting.it'
    },
    features: {
      enableLogging: false,
      enableAnalytics: true,
      enableHotjar: true
    }
  }
};

/**
 * Ottiene la configurazione per l'ambiente corrente
 * @returns {Object} Configurazione dell'ambiente
 */
export function getConfig() {
  const env = getEnvironment();
  const config = environments[env] || environments.development;
  
  console.debug(`Configurazione caricata per ambiente: ${config.name}`, { env, config });
  
  return config;
}

/**
 * Ottiene la configurazione GrowthBook per l'ambiente corrente
 * @returns {Object} Configurazione GrowthBook
 */
export function getGrowthBookConfig() {
  return getConfig().growthbook;
}

/**
 * Ottiene la configurazione API per l'ambiente corrente
 * @returns {Object} Configurazione API
 */
export function getApiConfig() {
  return getConfig().api;
}

/**
 * Verifica se siamo in ambiente di sviluppo
 * @returns {boolean} True se in sviluppo
 */
export function isDevelopment() {
  return getEnvironment() === 'development';
}

/**
 * Verifica se siamo in ambiente di staging
 * @returns {boolean} True se in staging
 */
export function isStaging() {
  return getEnvironment() === 'staging';
}

/**
 * Verifica se siamo in ambiente di produzione
 * @returns {boolean} True se in produzione
 */
export function isProduction() {
  return getEnvironment() === 'production';
}
